import React, { useState, useEffect, useRef } from "react";
import { Eye, EyeOff, Mail, Lock, ArrowRight, Droplets } from 'lucide-react';
import axios from "axios";
import { useNavigate } from "react-router-dom";
import SocialAuthentication from "../components/SocialAuthentication";
import config from '../config/config';

/* ─── Animated Water Canvas (Hero Panel) ─── */
function WaterCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let t = 0;

    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    window.addEventListener("resize", resize);

    const W = () => canvas.offsetWidth;
    const H = () => canvas.offsetHeight;

    // Bubble pool
    const bubbles = Array.from({ length: 28 }, () => ({
      x: Math.random() * 800,
      y: Math.random() * 600 + 600,
      r: 3 + Math.random() * 14,
      speed: 0.4 + Math.random() * 0.9,
      drift: (Math.random() - 0.5) * 0.4,
      opacity: 0.1 + Math.random() * 0.25,
    }));

    function draw() {

     
       t += 0.008;
  const w = W(), h = H();

  // ← ADD THIS GUARD
  if (!w || !h || !isFinite(w) || !isFinite(h)) {
    raf = requestAnimationFrame(draw);
    return;
  }

  ctx.clearRect(0, 0, w, h);

      // Deep gradient background
      const bg = ctx.createLinearGradient(0, 0, 0, h*0.32);
      bg.addColorStop(0, "#001f3f");
      bg.addColorStop(0.45, "#003a6b");
      bg.addColorStop(1, "#00162e");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      // Caustic light rays
      for (let i = 0; i < 5; i++) {
        const x = w * (0.1 + i * 0.2) + Math.sin(t * 0.7 + i) * 30;
        const g = ctx.createRadialGradient(x, -60, 0, x, h * 0.6, h * 0.8);
        g.addColorStop(0, `rgba(0,180,255,${0.06 + i * 0.01})`);
        g.addColorStop(1, "transparent");
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, h);
      }

      // Wave layers
      const waves = [
        { amp: 18, freq: 0.012, speed: 1.0, y: h * 0.62, color: "rgba(0,140,220,0.18)", fill: true },
        { amp: 13, freq: 0.018, speed: -1.3, y: h * 0.68, color: "rgba(0,180,255,0.13)", fill: true },
        { amp: 9,  freq: 0.025, speed: 0.8,  y: h * 0.74, color: "rgba(80,210,255,0.10)", fill: true },
        { amp: 22, freq: 0.009, speed: -0.6, y: h * 0.55, color: "rgba(0,100,180,0.12)", fill: true },
      ];

      waves.forEach(({ amp, freq, speed, y, color, fill }) => {
        ctx.beginPath();
        ctx.moveTo(0, h);
        for (let x = 0; x <= w; x += 2) {
          const wy = y + Math.sin(x * freq + t * speed) * amp
                       + Math.sin(x * freq * 1.7 + t * speed * 0.6) * (amp * 0.4);
          x === 0 ? ctx.moveTo(x, wy) : ctx.lineTo(x, wy);
        }
        if (fill) {
          ctx.lineTo(w, h); ctx.lineTo(0, h); ctx.closePath();
          ctx.fillStyle = color;
          ctx.fill();
        } else {
          ctx.strokeStyle = color; ctx.lineWidth = 1.5; ctx.stroke();
        }
      });

      // Bubbles
      bubbles.forEach(b => {
        b.y -= b.speed;
        b.x += b.drift;
        if (b.y + b.r < 0) {
          b.y = h + b.r;
          b.x = Math.random() * w;
        }
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(120,220,255,${b.opacity})`;
        ctx.lineWidth = 1;
        ctx.stroke();
        // Specular
        ctx.beginPath();
        ctx.arc(b.x - b.r * 0.28, b.y - b.r * 0.28, b.r * 0.22, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${b.opacity * 1.4})`;
        ctx.fill();
      });

      // Shimmer streaks
      for (let i = 0; i < 6; i++) {
        const sx = (w * 0.1 + i * w * 0.16 + Math.sin(t * 0.5 + i * 1.3) * 40) % w;
        const sy = h * 0.1 + Math.cos(t * 0.4 + i) * h * 0.1;
        const sl = 60 + Math.sin(t + i) * 30;
        const g2 = ctx.createLinearGradient(sx, sy, sx + sl * 0.4, sy + sl);
        g2.addColorStop(0, "transparent");
        g2.addColorStop(0.5, `rgba(180,240,255,${0.04 + i * 0.005})`);
        g2.addColorStop(1, "transparent");
        ctx.fillStyle = g2;
        ctx.fillRect(sx, sy, 2, sl);
      }

      raf = requestAnimationFrame(draw);
    }

    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);

  return <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />;
}

/* ─── Loader: Insigniah2o animated text ─── */
function InsigniaLoader() {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", gap: "16px"
    }}>
      <div style={{ position: "relative", width: 56, height: 56 }}>
        {/* Spinning ring */}
        <svg viewBox="0 0 56 56" style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", animation: "spinRing 1.4s linear infinite" }}>
          <circle cx="28" cy="28" r="24" fill="none" stroke="rgba(0,160,240,0.18)" strokeWidth="3" />
          <circle cx="28" cy="28" r="24" fill="none" stroke="#00c6ff" strokeWidth="3"
            strokeDasharray="50 102" strokeLinecap="round" />
        </svg>
        {/* Drop icon */}
        <div style={{
          position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
            <path d="M12 2C12 2 5 9.5 5 14a7 7 0 0 0 14 0C19 9.5 12 2 12 2z"
              fill="url(#dg)" style={{ animation: "dropPulse 1.4s ease-in-out infinite" }} />
            <defs>
              <linearGradient id="dg" x1="12" y1="2" x2="12" y2="21" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#00c6ff" />
                <stop offset="100%" stopColor="#0072ff" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>
      {/* Animated brand text */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 0 }}>
        {"Insignia".split("").map((ch, i) => (
          <span key={i} style={{
            fontFamily: "Quicksand", fontWeight: 700,
            fontSize: 15, color: "#0a1628", letterSpacing: "0.01em",
            animation: `letterBounce 1.4s ease-in-out infinite`,
            animationDelay: `${i * 0.07}s`, display: "inline-block"
          }}>{ch}</span>
        ))}
        <span style={{
          fontFamily: "Quicksand", fontWeight: 800,
          fontSize: 15, color: "#00c6ff", letterSpacing: "0.01em",
          animation: "letterBounce 1.4s ease-in-out infinite",
          animationDelay: "0.56s", display: "inline-block"
        }}>h</span>
        <span style={{
          fontFamily: "Quicksand", fontWeight: 800,
          fontSize: 15, color: "#00c6ff",
          animation: "letterBounce 1.4s ease-in-out infinite",
          animationDelay: "0.63s", display: "inline-block"
        }}>2</span>
        <span style={{
          fontFamily: "Quicksand", fontWeight: 800,
          fontSize: 15, color: "#00c6ff",
          animation: "letterBounce 1.4s ease-in-out infinite",
          animationDelay: "0.70s", display: "inline-block"
        }}>o</span>
      </div>
      <span style={{
        fontFamily: "Quicksand", fontSize: 11,
        color: "rgba(80,120,180,0.6)", letterSpacing: "0.12em",
        textTransform: "uppercase", marginTop: -8
      }}>Signing in…</span>
    </div>
  );
}

/* ─── Main LoginForm ─── */
function LoginForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Load Sora font
    const link = document.createElement("link");
    link.href = "https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    setTimeout(() => setMounted(true), 60);
    return () => document.head.removeChild(link);
  }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });
      const getCookie = (name) => {
        const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
        if (match) return decodeURIComponent(match[2]);
        return null;
      };
      const xsrfToken = getCookie("XSRF-TOKEN");
      const response = await axios.post(config.API_URL + "/api/apiLogin", formData, {
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-XSRF-TOKEN": xsrfToken },
        withCredentials: true,
      });
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("userInfor", response.data.user);
      if (response.data.status == 1) {
        setSuccess("Successfully logged in. Redirecting...");
        //alert(response.data.user?.role);
       if (response.data.user?.system_role == "Admin") window.location.assign('https://finance.insigniah2o.co.ke/cms/public/authenticate-users/'+response.data.token);
       else if (response.data.user?.system_role == "Biller") navigate("/StartShift");
       else if (response.data.user?.system_role == "Teacher") navigate("/Assessment");
       else window.location.assign('https://finance.insigniah2o.co.ke/cms/public/authenticate-users/'+response.data.token);
      } else {
        setError("Invalid credentials. Please try again.");
        navigate("/");
      }
    } catch {
      setError("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const styles = `
    @keyframes spinRing { to { transform: rotate(360deg); } }
    @keyframes dropPulse { 0%,100%{opacity:.7;transform:scale(1)} 50%{opacity:1;transform:scale(1.12)} }
    @keyframes letterBounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
    @keyframes fadeUp { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:translateY(0)} }
    @keyframes floatDrop { 0%,100%{transform:translateY(0) rotate(-6deg)} 50%{transform:translateY(-12px) rotate(-6deg)} }
    @keyframes pulseGlow { 0%,100%{opacity:.4} 50%{opacity:.75} }
    @keyframes ripple { 0%{transform:scale(0.7);opacity:.7} 100%{transform:scale(2.2);opacity:0} }

    .ih2o-input {
      width: 100%; box-sizing: border-box;
      background: #f7f9fc;
      border: 1px solid #dce3ef;
      border-radius: 12px;
      color: #0a1628;
      font-family: 'Sora', sans-serif;
      font-size: 14px;
      padding: 13px 16px 13px 44px;
      outline: none;
      transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
    }
    .ih2o-input::placeholder { color: #b0bcc9; }
    .ih2o-input:focus {
      border-color: #0072ff;
      box-shadow: 0 0 0 3px rgba(0,114,255,0.1);
      background: #ffffff;
    }
    .ih2o-input:-webkit-autofill {
      -webkit-box-shadow: 0 0 0 100px #f7f9fc inset !important;
      -webkit-text-fill-color: #0a1628 !important;
    }
    .ih2o-btn {
      width: 100%; border: none; cursor: pointer;
      background: linear-gradient(135deg, #0072ff 0%, #00c6ff 100%);
      color: #fff; font-family: 'Sora', sans-serif; font-weight: 700;
      font-size: 15px; letter-spacing: 0.04em;
      padding: 14px 24px; border-radius: 12px;
      display: flex; align-items: center; justify-content: center; gap: 10px;
      box-shadow: 0 4px 24px rgba(0,114,255,0.35);
      transition: transform 0.15s, box-shadow 0.15s, opacity 0.15s;
      position: relative; overflow: hidden;
    }
    .ih2o-btn:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 32px rgba(0,114,255,0.5);
    }
    .ih2o-btn:active:not(:disabled) { transform: translateY(0); }
    .ih2o-btn:disabled { opacity: 0.65; cursor: not-allowed; }
    .ih2o-btn::after {
      content: ''; position: absolute; inset: 0;
      background: linear-gradient(180deg, rgba(255,255,255,0.12) 0%, transparent 60%);
      pointer-events: none;
    }
    .hero-stat { animation: fadeUp 0.7s ease both; }
    .field-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); pointer-events: none; transition: color 0.2s; }
  `;

  return (
    <>
      <style>{styles}</style>
      <div style={{
        display: "flex", minHeight: "100vh", width: "100%",
        fontFamily: "Quicksand",
        background: "#00162e",
      }}>

        {/* ── LEFT: Hero panel (desktop only) ── */}
        <div style={{
          display: "none", flex: "0 0 52%", position: "relative", overflow: "hidden",
          ...(typeof window !== "undefined" && window.innerWidth >= 900 ? { display: "flex" } : {}),
        }}
          className="hero-panel"
        >
          <style>{`.hero-panel { display: none; } @media(min-width:900px){ .hero-panel { display: flex !important; flex-direction: column; position: relative; overflow: hidden; flex: 0 0 52%; } }`}</style>
          
          {/* Water canvas */}
          <div style={{ position: "absolute", inset: 0 }}>
            <WaterCanvas />
          </div>

          {/* Overlay content */}
          <div style={{
            position: "relative", zIndex: 2, display: "flex", flexDirection: "column",
            justifyContent: "center", padding: "60px 56px", height: "100%",
          }}>
            {/* Brand */}
            <div style={{ marginBottom: 48 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                <div style={{ position: "relative", width: 44, height: 44 }}>
                  <div style={{
                    position: "absolute", inset: 0, borderRadius: "50%",
                    background: "radial-gradient(circle, rgba(0,198,255,0.25) 0%, transparent 70%)",
                    animation: "ripple 2.5s ease-out infinite",
                  }} />
                  <Droplets size={28} color="#00c6ff" style={{ position: "relative", zIndex: 1, marginTop: 8, marginLeft: 8 }} />
                </div>
                <div>
                  <span style={{ fontWeight: 800, fontSize: 22, color: "#e0f4ff", letterSpacing: "-0.01em" }}>
                    Insignia
                  </span>
                  <span style={{ fontWeight: 800, fontSize: 22, color: "#00c6ff" }}>h₂o</span>
                </div>
              </div>

              <h1 style={{
                fontWeight: 800, fontSize: "clamp(28px, 3.5vw, 42px)",
                color: "#fff", lineHeight: 1.15, margin: "0 0 16px",
                textShadow: "0 2px 20px rgba(0,100,200,0.4)"
              }}>
                Water Sales<br />
                <span style={{
                  background: "linear-gradient(90deg, #00c6ff, #7de8ff)",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent"
                }}>Management</span><br />
                Made Simple.
              </h1>
              <p style={{
                color: "rgba(160,220,255,0.7)", fontSize: 15, lineHeight: 1.7,
                maxWidth: 360, margin: 0, fontWeight: 400
              }}>
                Track sales, manage inventory, and monitor your water distribution points — all from one intelligent dashboard.
              </p>
            </div>

            {/* Stats */}
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              {[
                { value: "99.9%", label: "Uptime", delay: "0s" },
                { value: "Real-time", label: "Reporting", delay: "0.1s" },
                { value: "Multi-point", label: "Management", delay: "0.2s" },
              ].map(({ value, label, delay }) => (
                <div key={label} className="hero-stat" style={{
                  animationDelay: delay,
                  background: "rgba(0,40,90,0.5)",
                  border: "1px solid rgba(0,160,255,0.18)",
                  borderRadius: 14, padding: "14px 20px", backdropFilter: "blur(8px)",
                }}>
                  <div style={{ fontWeight: 800, fontSize: 20, color: "#00c6ff" }}>{value}</div>
                  <div style={{ fontSize: 11, color: "rgba(160,220,255,0.55)", textTransform: "uppercase", letterSpacing: "0.1em", marginTop: 2 }}>{label}</div>
                </div>
              ))}
            </div>

            {/* Floating drop decoration */}
            <div style={{
              position: "absolute", bottom: 60, right: 48,
              animation: "floatDrop 4s ease-in-out infinite",
              opacity: 0.18,
            }}>
              <svg viewBox="0 0 80 100" width="80" fill="url(#heroDropGrad)">
                <path d="M40 4C40 4 6 38 6 62a34 34 0 0 0 68 0C74 38 40 4 40 4z" />
                <defs>
                  <linearGradient id="heroDropGrad" x1="40" y1="4" x2="40" y2="96" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#00c6ff" />
                    <stop offset="100%" stopColor="#0040a0" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>
        </div>

        {/* ── RIGHT: Login form panel ── */}
        <div style={{
          flex: 1, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          minHeight: "100vh", padding: "32px 20px",
          background: "#ffffff",
          position: "relative", overflow: "hidden",
        }}>
          {/* Subtle background glow */}
          <div style={{
            position: "absolute", top: "20%", left: "50%", transform: "translateX(-50%)",
            width: 400, height: 400,
            background: "radial-gradient(circle, rgba(0,114,255,0.05) 0%, transparent 70%)",
            pointerEvents: "none", animation: "pulseGlow 5s ease-in-out infinite",
          }} />

          <div style={{
            width: "100%", maxWidth: 420, position: "relative", zIndex: 1,
            opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(18px)",
            transition: "opacity 0.55s ease, transform 0.55s ease",
          }}>
            {/* Mobile-only brand header */}
            <div style={{ textAlign: "center", marginBottom: 36 }} className="mobile-brand">
              <style>{`.mobile-brand { display: flex; flex-direction: column; align-items: center; } @media(min-width:900px){ .mobile-brand { display: none; } }`}</style>
              <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", marginBottom: 8 }}>
                <Droplets size={22} color="#0072ff" />
                <span style={{ fontWeight: 800, fontSize: 20, color: "#0a1628" }}>Insignia</span>
                <span style={{ fontWeight: 800, fontSize: 20, color: "#0072ff" }}>h₂o</span>
              </div>
              <span style={{ fontSize: 12, color: "#a0aec0", letterSpacing: "0.12em", textTransform: "uppercase" }}>
                Water Sales Management
              </span>
            </div>

            {/* Card */}
            <div style={{
              background: "#ffffff",
              border: "1px solid rgba(0,114,255,0.1)",
              borderRadius: 24, padding: "36px 32px",
              boxShadow: "0 8px 48px rgba(0,80,200,0.08), 0 1px 3px rgba(0,0,0,0.06)",
            }}>
              {/* Card header */}
              <div style={{ marginBottom: 28 }}>
                <h2 style={{
                  margin: "0 0 6px", fontWeight: 700, fontSize: 22,
                  color: "#0a1628", letterSpacing: "-0.02em"
                }}>Welcome back</h2>
                <p style={{ margin: 0, fontSize: 13, color: "#8a9ab5", fontWeight: 400 }}>
                  Sign in to your account to continue
                </p>
              </div>

              {/* Social Auth */}
              <div style={{ marginBottom: 22 }}>
                <SocialAuthentication />
              </div>

              {/* Divider */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
                <div style={{ flex: 1, height: 1, background: "#e8edf5" }} />
                <span style={{ fontSize: 11, color: "#b0bcc9", letterSpacing: "0.1em", textTransform: "uppercase" }}>or</span>
                <div style={{ flex: 1, height: 1, background: "#e8edf5" }} />
              </div>

              {/* Loader overlay */}
              {loading && (
                <div style={{
                  position: "absolute", inset: 0, borderRadius: 24, zIndex: 10,
                  background: "rgba(255,255,255,0.92)", backdropFilter: "blur(8px)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <InsigniaLoader />
                </div>
              )}

              {/* Alerts */}
              {success && (
                <div style={{
                  marginBottom: 18, padding: "12px 16px", borderRadius: 10,
                  background: "#f0fdf7", border: "1px solid #a7f3d0",
                  display: "flex", alignItems: "center", gap: 10,
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#10b981" strokeWidth="2"/><path d="M8 12l3 3 5-5" stroke="#10b981" strokeWidth="2" strokeLinecap="round"/></svg>
                  <span style={{ fontSize: 13, color: "#065f46", fontWeight: 500 }}>{success}</span>
                </div>
              )}
              {error && (
                <div style={{
                  marginBottom: 18, padding: "12px 16px", borderRadius: 10,
                  background: "#fff5f5", border: "1px solid #fecaca",
                  display: "flex", alignItems: "center", gap: 10,
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/><path d="M12 8v4m0 4h.01" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/></svg>
                  <span style={{ fontSize: 13, color: "#991b1b", fontWeight: 500 }}>{error}</span>
                </div>
              )}

              {/* Form fields */}
              <div style={{ display: "flex", flexDirection: "column", gap: 16, position: "relative" }}>
                {/* Email */}
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#4b607a", marginBottom: 7, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                    Email Address
                  </label>
                  <div style={{ position: "relative" }}>
                    <span className="field-icon">
                      <Mail size={16} color={focusedField === "email" ? "#0072ff" : "#a0aec0"} />
                    </span>
                    <input
                      type="email" name="email" placeholder="you@example.com"
                      autoComplete="email" value={formData.email}
                      onChange={handleChange}
                      onFocus={() => setFocusedField("email")}
                      onBlur={() => setFocusedField(null)}
                      className="ih2o-input" required
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#4b607a", marginBottom: 7, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                    Password
                  </label>
                  <div style={{ position: "relative" }}>
                    <span className="field-icon">
                      <Lock size={16} color={focusedField === "password" ? "#0072ff" : "#a0aec0"} />
                    </span>
                    <input
                      type={showPassword ? "text" : "password"} name="password"
                      placeholder="Enter your password" autoComplete="current-password"
                      value={formData.password} onChange={handleChange}
                      onFocus={() => setFocusedField("password")}
                      onBlur={() => setFocusedField(null)}
                      className="ih2o-input" required
                      style={{ paddingRight: 44 }}
                    />
                    <button
                      type="button" onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)",
                        background: "none", border: "none", cursor: "pointer", padding: 0,
                        color: "#a0aec0", display: "flex", transition: "color 0.15s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.color = "#0072ff"}
                      onMouseLeave={e => e.currentTarget.style.color = "#a0aec0"}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit" disabled={loading}
                  onClick={handleSubmit}
                  className="ih2o-btn"
                  style={{ marginTop: 6 }}
                >
                  {loading ? (
                    <span>Please wait…</span>
                  ) : (
                    <>
                      <span>Sign In</span>
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Footer */}
            <p style={{
              textAlign: "center", marginTop: 24, fontSize: 11,
              color: "#b0bcc9", letterSpacing: "0.05em",
            }}>
              © {new Date().getFullYear()} Insigniah₂o · Water Sales Management
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default LoginForm;
